import {
  ChevronLeft,
  ChevronRight,
  FolderKanban,
  Pencil,
  Star,
  StarOff,
  Trash2,
} from 'lucide-react';
import { type RefObject, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../lib/cn';
import { useProjects } from '../lib/projects';
import type { SessionRow } from '../lib/sessions';

type Props = {
  session: SessionRow;
  onStar: (next: boolean) => void;
  onRename: () => void;
  onSetProject: (projectId: string | null) => void;
  onDelete: () => void;
  onClose: () => void;
  /** Kebab button — menu is portalled so Recents overflow doesn't clip it. */
  anchorRef: RefObject<HTMLElement | null>;
};

type MenuBox = {
  left: number;
  maxHeight: number;
  top?: number;
  bottom?: number;
};

const MENU_WIDTH = 220;
const MENU_GAP = 4;
const MENU_MARGIN = 8;

function measureMenu(anchor: HTMLElement): MenuBox {
  const r = anchor.getBoundingClientRect();
  const width = Math.min(window.innerWidth - MENU_MARGIN * 2, MENU_WIDTH);
  const left = Math.min(
    Math.max(MENU_MARGIN, r.right - width),
    window.innerWidth - width - MENU_MARGIN,
  );
  const spaceBelow = window.innerHeight - r.bottom - MENU_MARGIN;
  const spaceAbove = r.top - MENU_MARGIN;
  const openUp = spaceBelow < 200 && spaceAbove > spaceBelow;
  if (openUp) {
    return {
      left,
      bottom: window.innerHeight - r.top + MENU_GAP,
      maxHeight: Math.max(120, spaceAbove),
    };
  }
  return {
    left,
    top: r.bottom + MENU_GAP,
    maxHeight: Math.max(120, spaceBelow),
  };
}

type Mode = 'main' | 'projects' | 'confirm-delete';

/**
 * Popover menu for a sidebar chat row (claude.ai parity):
 *   В избранное · Переименовать · Добавить в проект · Удалить.
 *
 * "Add to project" swaps the body to a list of the user's projects
 * plus "No project" — nested popovers on a mobile drawer are worse
 * UX than switching in-place.
 */
export function SessionRowMenu({
  session,
  onStar,
  onRename,
  onSetProject,
  onDelete,
  onClose,
  anchorRef,
}: Props) {
  const [mode, setMode] = useState<Mode>('main');
  const wrapRef = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState<MenuBox | null>(() =>
    anchorRef.current ? measureMenu(anchorRef.current) : null,
  );

  useLayoutEffect(() => {
    const place = (): void => {
      const anchor = anchorRef.current;
      if (anchor) setBox(measureMenu(anchor));
    };
    place();
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  }, [anchorRef]);

  useEffect(() => {
    const first = mode === 'main' ? '[role="menuitem"]' : 'button';
    wrapRef.current?.querySelector<HTMLElement>(first)?.focus();
  }, [mode]);

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      const t = e.target as Node;
      if (wrapRef.current?.contains(t)) return;
      if (anchorRef.current?.contains(t)) return;
      onClose();
    }
    function onKey(e: KeyboardEvent) {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      if (mode === 'main') {
        anchorRef.current?.focus();
        onClose();
      } else setMode('main');
    }
    function onScroll(e: Event): void {
      if (wrapRef.current && e.target instanceof Node && wrapRef.current.contains(e.target)) {
        return;
      }
      onClose();
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey, true);
    document.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('scroll', onScroll, true);
    };
  }, [mode, onClose, anchorRef]);

  if (!box) return null;

  return createPortal(
    <div
      ref={wrapRef}
      role="menu"
      aria-label="Действия с чатом"
      className="mc-menu-enter origin-top fixed z-50 w-[min(calc(100vw-2rem),220px)] overflow-y-auto rounded-2xl border border-border/70 bg-surface p-1.5 text-[14px] mc-float"
      style={{
        left: box.left,
        top: box.top,
        bottom: box.bottom,
        maxHeight: box.maxHeight,
      }}
    >
      {mode === 'main' && (
        <MainBody
          starred={session.starred > 0}
          onStar={() => {
            onStar(session.starred === 0);
            anchorRef.current?.focus();
            onClose();
          }}
          onRename={() => {
            onRename();
            onClose();
          }}
          onAddToProject={() => setMode('projects')}
          onDelete={() => setMode('confirm-delete')}
        />
      )}
      {mode === 'projects' && (
        <ProjectsBody
          currentProjectId={session.project_id}
          onBack={() => setMode('main')}
          onPick={(id) => {
            onSetProject(id);
            anchorRef.current?.focus();
            onClose();
          }}
        />
      )}
      {mode === 'confirm-delete' && (
        <ConfirmDeleteBody
          title={session.title}
          onCancel={() => setMode('main')}
          onConfirm={() => {
            onDelete();
            onClose();
          }}
        />
      )}
    </div>,
    anchorRef.current?.closest('aside') ?? document.body,
  );
}

function MainBody({
  starred,
  onStar,
  onRename,
  onAddToProject,
  onDelete,
}: {
  starred: boolean;
  onStar: () => void;
  onRename: () => void;
  onAddToProject: () => void;
  onDelete: () => void;
}) {
  return (
    <>
      <Item
        icon={starred ? <StarOff size={16} /> : <Star size={16} />}
        label={starred ? 'Убрать из избранного' : 'В избранное'}
        onClick={onStar}
      />
      <Item icon={<Pencil size={16} />} label="Переименовать" onClick={onRename} />
      <Item
        icon={<FolderKanban size={16} />}
        label="Добавить в проект"
        chevron
        onClick={onAddToProject}
      />
      <div className="my-1 h-px bg-border" />
      <Item icon={<Trash2 size={16} />} label="Удалить" destructive onClick={onDelete} />
    </>
  );
}

function ProjectsBody({
  currentProjectId,
  onBack,
  onPick,
}: {
  currentProjectId: string | null;
  onBack: () => void;
  onPick: (projectId: string | null) => void;
}) {
  const { data: projects, isLoading } = useProjects();
  return (
    <>
      <button
        type="button"
        onClick={onBack}
        className="mb-1 flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[13px] text-muted-foreground hover:bg-subtle"
      >
        <ChevronLeft size={14} />
        Назад
      </button>
      <Item label="Без проекта" onClick={() => onPick(null)} active={currentProjectId === null} />
      {isLoading && <div className="px-3 py-2 text-[12px] text-muted-foreground">Загрузка…</div>}
      {projects && projects.length === 0 && !isLoading && (
        <div className="px-3 py-2 text-[12px] text-muted-foreground">Проектов пока нет.</div>
      )}
      {projects?.map((p) => (
        <Item
          key={p.id}
          label={p.name}
          onClick={() => onPick(p.id)}
          active={currentProjectId === p.id}
        />
      ))}
    </>
  );
}

function ConfirmDeleteBody({
  title,
  onCancel,
  onConfirm,
}: {
  title: string;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div className="p-1">
      <div className="px-2 pt-1 pb-2 text-[13px]">
        Удалить <span className="font-medium">{title}</span>? Это нельзя отменить.
      </div>
      <button
        type="button"
        // biome-ignore lint/a11y/noAutofocus: confirm is the only action
        autoFocus
        onClick={onConfirm}
        className="flex w-full items-center justify-center gap-2 rounded-md bg-destructive px-3 py-2 text-sm font-medium text-destructive-foreground hover:opacity-90"
      >
        <Trash2 size={14} />
        Удалить
      </button>
      <button
        type="button"
        onClick={onCancel}
        className="mt-1 flex w-full items-center justify-center rounded-md px-3 py-2 text-sm text-muted-foreground hover:bg-subtle"
      >
        Отмена
      </button>
    </div>
  );
}

function Item({
  icon,
  label,
  onClick,
  chevron = false,
  active = false,
  destructive = false,
}: {
  icon?: React.ReactNode;
  label: string;
  onClick: () => void;
  chevron?: boolean;
  active?: boolean;
  destructive?: boolean;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className={cn(
        'flex w-full items-center gap-3 rounded-md px-3 py-2 text-left hover:bg-subtle',
        active && 'bg-subtle',
        destructive && 'text-destructive hover:bg-destructive/10',
      )}
    >
      {icon ? <span className="shrink-0">{icon}</span> : null}
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {chevron && <ChevronRight size={14} className="shrink-0 text-muted-foreground" />}
    </button>
  );
}
